<script setup lang="ts">
import { useCustomizerStore } from "@/stores/customizer";
import { Icon } from "@iconify/vue";
import { ref, shallowRef } from "vue";
import { useRoute } from "vue-router";
import Logo from "../logo/Logo.vue";
import NavCollapse from "./NavCollapse/NavCollapse.vue";
import NavGroup from "./NavGroup/index.vue";
import NavItem from "./NavItem/index.vue";
import sidebarItems, { type menu } from "./sidebarItem";
// MiniSidebar Icons
import { DBLocalMenu } from "@/enumLocal/menu";
import { useStoreUser } from "@/stores/storeUser";
import loggerLocal from "@/utils/loggerLocal";
import { DBAuthProfile, DBRole } from "@functions/models/DBEnum";
import { version } from './../../../../package.json';
import MiniSideIcons, { type minisidebar } from "./MinIconItems";
const { t: $t } = useI18n();
const route = useRoute();

const findTitleByPath = (items: any, path: any) => {
  let title = "";

  for (const item of items) {
    if (item.to === path) {
      title = item.id;
      break;
    } else if (item.children) {
      for (const child of item.children) {
        if (child.to === path) {
          title = item.id;
          break;
        } else if (child.children) {
          for (const grandChild of child.children) {
            if (grandChild.to === path) {
              title = item.id;
              break;
            }
          }
        }
      }
    }
  }

  return title;
};

const findTitleByRouteName = (items: any[], name: any): string => {
  for (const item of items) {
    const to = item.to;
    if (to && typeof to === 'string' && to === name) return item.id;
    if (to && typeof to === 'object' && 'name' in to && to.name === name) return item.id;
    if (item.children) {
      const r = findTitleByRouteName(item.children, name);
      if (r) return r;
    }
  }
  return '';
};

//const foundId = findTitleByPath(sidebarItems, route.path);
const foundId = findTitleByRouteName(sidebarItems, route.name);

const getCurrent = foundId ? foundId : DBLocalMenu.HOME;
const currentMenu = ref<any>(getCurrent);
function showData(data: any) {
  loggerLocal.debug("showData", data, customizer.Sidebar_drawer);
  //customizer.Sidebar_drawer = true;

  customizer.SET_MINI_SIDEBAR(false);
  currentMenu.value = data;
  //customizer.SET_MINI_SIDEBAR(!customizer.mini_sidebar)
}

// MiniSidebar Icons End
const customizer = useCustomizerStore();
const sidebarMenu = shallowRef(sidebarItems);
const storeUser = useStoreUser();
const newMiniSideIcons = computed(() => {
  const companyAuth = storeUser?.companyData?.authProfileId ?? DBAuthProfile.CLIENT;
  const userRole = storeUser?.userCompanyData?.role ?? DBRole.USER;
  return MiniSideIcons.filter((item: minisidebar) => {
    if (
        item.authProfile.includes(companyAuth) &&
      item.role.includes(userRole)
    ) {
      return true;
    }
    return false;
  });
});

const newSideBarMenu = computed(() => {
  const companyAuth = storeUser?.companyData?.authProfileId ?? DBAuthProfile.CLIENT;
  const userRole = storeUser?.userCompanyData?.role ?? DBRole.USER;

  return sidebarMenu.value.filter((item: menu) => {
    if (item.authProfile && item.authProfile.includes(companyAuth) &&
      item.role && item.role.includes(userRole)) {
      return true;
    }
    return false;
  });
});
</script>

<template>
  <!-- Minisidebar Icons -->
  <v-navigation-drawer
    class="bg-light"
    v-model="customizer.Sidebar_drawer"
    top="0"
    rail
    rail-width="80"
  >
    <perfect-scrollbar class="miniscrollnavbar">
      <v-list-item class="px-0">
        <!-- Toggle Sidebar Button -->
        <div class="px-4 mb-3">
          <v-btn
            class="hidden-md-and-down my-2"
            icon
            rounded="md"
            variant="plain"
            @click.stop="customizer.SET_MINI_SIDEBAR(!customizer.mini_sidebar)"
          >
            <Icon icon="solar:hamburger-menu-line-duotone" height="25" />
          </v-btn>
        </div>

        <div class="miniicons mt-lg-0 mt-4">
          <!-- MiniSidebar Icons -->
          <div class="d-flex flex-column gap-2">
            <div
              class="miniicons-list px-4"
              v-for="menu in newMiniSideIcons"
              :key="menu.icon"
            >
              <v-btn
                rounded="md"
                flat
                icon
                variant="plain"
                @click="showData(menu.id)"
                :class="{ 'bg-primary opacity-1': currentMenu === menu.id }"
              >
                <Icon :icon="'solar:' + menu.icon" width="25" />
                <!-- Tooltip on Hover -->
                <v-tooltip activator="parent" location="end" class="custom-tooltip">{{
                  menu.tooltip
                }}</v-tooltip>
              </v-btn>
            </div>
          </div>

          <div class="mt-4 text-center text-subtitle-2 textSecondary">
            {{ $t("generic.versionMin", { version }) }}
          </div>
        </div>
      </v-list-item>
    </perfect-scrollbar>
  </v-navigation-drawer>

  <!-- LeftSidebar Items -->
  <v-navigation-drawer
    v-model="customizer.Sidebar_drawer"
    elevation="0"
    rail-width="1"
    app
    top="0"
    class="leftSidebar"
    :rail="customizer.mini_sidebar"
    width="240"
  >
    <!---Logo part -->
    <div class="pa-4 pb-0">
      <Logo />
    </div>

    <!-- ---------------------------------------------- -->
    <!---Navigation -->
    <!-- ---------------------------------------------- -->

    <perfect-scrollbar class="scrollnavbar">
      <div class="px-4 py-0 sidebar-menus">
        <v-list class="py-1">
          <template v-for="(item, i) in newSideBarMenu">
            <template v-if="currentMenu == item.id">
              <!---Item Sub Header -->
              <NavGroup :item="item" v-if="item.header" :key="item.title" />
              <!---If Has Child -->
              <template v-for="sItem in item.children">
                <NavCollapse
                  class="leftPadding"
                  :item="sItem"
                  :level="0"
                  v-if="sItem.children"
                />
                <NavItem :item="sItem" class="leftPadding" v-else />
              </template>
            </template>
          </template>
        </v-list>
      </div>
    </perfect-scrollbar>
  </v-navigation-drawer>
</template>
